import { useEffect, useState, useRef, useCallback } from 'react';
import { getMercadoPagoPublicKey, isTestMode } from '../../config/mercadopagoConfig';
import './MercadoPagoBricks.css';
import { log, logError, warn } from '../../utils/logger';

/**
 * Componente de pago con MercadoPago Bricks
 * 
 * Este componente maneja el pago embebido usando Checkout Bricks
 * El usuario no sale de la página para completar el pago
 */
function isBrickValidationError(error) {
  return (
    error?.type === 'non_critical' &&
    (error?.cause === 'incomplete_fields' || error?.cause === 'invalid_fields')
  );
}

const MercadoPagoBricks = ({ 
  amount, 
  items, 
  onSuccess, 
  onError,
  onReady,
  disabled = false,
  /** Sin caja externa duplicada (p. ej. dentro de CDF-wrap en el modal del carrito) */
  embedded = false,
  /**
   * Oculta el botón del brick y delega el envío a `getFormData()` (p. ej. botón en el footer del modal).
   * Al estar listo, llama `onExternalSubmitReady({ submit: () => Promise<CardData> })` o `null` al limpiar.
   */
  externalSubmitButton = false,
  onExternalSubmitReady,
}) => {
  const [publicKey, setPublicKey] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [mpError, setMpError] = useState(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const bricksBuilderRef = useRef(null);
  /** Instancia devuelta por bricks().create(); es la que expone unmount(), no mp.bricks() */
  const brickControllerRef = useRef(null);
  const containerRef = useRef(null);
  const initializationAttempted = useRef(false);
  const initializedAmountRef = useRef(null);
  const showTestInfo = isTestMode() && process.env.NODE_ENV !== 'development';

  const destroyBrick = useCallback(() => {
    const ctrl = brickControllerRef.current;
    brickControllerRef.current = null;
    bricksBuilderRef.current = null;
    initializedAmountRef.current = null;
    if (ctrl) {
      try {
        if (typeof ctrl.unmount === 'function') {
          ctrl.unmount();
        } else if (typeof ctrl.destroy === 'function') {
          ctrl.destroy();
        }
      } catch (e) {
        warn('Error al desmontar Bricks:', e);
      }
    }
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
  }, []);

  // Cargar SDK de MercadoPago
  useEffect(() => {
    let script = null;
    let mounted = true;

    const loadSDK = () => {
      // Verificar si el SDK ya está cargado
      if (window.MercadoPago) {
        log('✅ SDK de MercadoPago ya está cargado');
        setSdkLoaded(true);
        // Esperar un momento para asegurar que el DOM esté listo
        // No inicializar aquí, el useEffect se encargará
        return;
      }

      // Cargar el SDK desde el CDN
      script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.async = true;
      script.onload = () => {
        log('✅ SDK de MercadoPago cargado desde CDN');
        if (mounted) {
          setSdkLoaded(true);
          // No inicializar aquí, el useEffect se encargará
        }
      };
      script.onerror = () => {
        logError('❌ Error cargando SDK de MercadoPago');
        if (mounted) {
          setMpError('Error al cargar MercadoPago. Por favor, recarga la página.');
        }
      };
      document.head.appendChild(script);
    };

    loadSDK();

    return () => {
      mounted = false;
      destroyBrick();
      // No remover el script global del SDK: es compartido y quitarlo provoca "Script error." al reabrir.
    };
  }, [destroyBrick]);

  const initializeBricks = useCallback(() => {
    if (initializationAttempted.current) {
      log('⚠️ Inicialización ya intentada, omitiendo...');
      return;
    }

    initializationAttempted.current = true;
    log('🔧 Inicializando Bricks...');
    
    const key = getMercadoPagoPublicKey();
    
    if (!key) {
      logError('❌ Public Key no encontrada');
      setMpError('Public Key de MercadoPago no configurada. Verifica tu archivo .env');
      initializationAttempted.current = false;
      return;
    }

    if (!window.MercadoPago) {
      logError('❌ SDK de MercadoPago no disponible');
      setMpError('SDK de MercadoPago no disponible');
      initializationAttempted.current = false;
      return;
    }

      // Verificar que el contenedor esté disponible y visible
      if (!containerRef.current) {
        warn('⚠️ Contenedor no disponible aún, reintentando...');
        initializationAttempted.current = false;
        setTimeout(() => {
          if (containerRef.current && !initialized) {
            initializeBricks();
          }
        }, 300);
        return;
      }

      // Verificar que el contenedor esté visible (importante para modales)
      if (containerRef.current.offsetParent === null) {
        warn('⚠️ Contenedor no visible aún (puede estar en un modal), esperando...');
        initializationAttempted.current = false;
        setTimeout(() => {
          if (containerRef.current && containerRef.current.offsetParent !== null && !initialized) {
            initializeBricks();
          }
        }, 500);
        return;
      }

    log('✅ Contenedor disponible, creando Brick...');
    setPublicKey(key);

    try {
      const mp = new window.MercadoPago(key, {
        locale: 'es-AR'
      });

      if (isTestMode()) {
        log('🧪 Modo PRUEBA activado');
      }

      // Limpiar contenedor antes de crear el Brick
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      // Verificar que mp.bricks() existe
      if (!mp.bricks || typeof mp.bricks !== 'function') {
        throw new Error('mp.bricks() no está disponible');
      }

      // Crear el Brick de pago con tarjeta
      bricksBuilderRef.current = mp.bricks();
      
      if (!bricksBuilderRef.current || typeof bricksBuilderRef.current.create !== 'function') {
        throw new Error('mp.bricks().create no está disponible');
      }
      
      log('📦 Creando cardPayment Brick...');
      log('   Amount:', amount);
      log('   Container:', containerRef.current);
      log('   Container visible:', containerRef.current.offsetParent !== null);
      log('   Container dimensions:', {
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight
      });
      log('   Container in DOM:', document.body.contains(containerRef.current));
      
      // Asegurar que el contenedor tenga un ID
      let containerId = containerRef.current.id || 'mp-bricks-container-' + Date.now();
      if (!containerRef.current.id) {
        containerRef.current.id = containerId;
      }

      // Timeout de seguridad - si onReady no se llama en 10 segundos, mostrar error
      const readyTimeout = setTimeout(() => {
        if (!initialized) {
          logError('⏱️ Timeout: onReady no se llamó después de 10 segundos');
          setMpError('El formulario de pago está tardando demasiado en cargar. Por favor, recarga la página.');
          initializationAttempted.current = false;
        }
      }, 10000);

      try {
        // Verificar que mp.bricks() existe
        if (!bricksBuilderRef.current || typeof bricksBuilderRef.current.create !== 'function') {
          throw new Error('mp.bricks().create no está disponible');
        }

        // Asegurar que amount sea un número válido y mayor a 0
        const paymentAmount = parseFloat(String(amount));
        if (isNaN(paymentAmount) || paymentAmount <= 0) {
          throw new Error(`Amount inválido: ${amount}. Debe ser un número mayor a 0.`);
        }

        // Asegurar que currency sea un string
        const currency = 'ARS';

        // Variables alineadas a CheckoutDeliveryForm (.CDF-control): borde slate-200, foco cyan, tipografía slate
        const checkoutAlignedBrickVariables = {
          baseColor: '#06b6d4',
          baseColorFirstVariant: '#0891b2',
          baseColorSecondVariant: '#0e7490',
          outlinePrimaryColor: '#e2e8f0',
          outlineSecondaryColor: '#cbd5e1',
          buttonTextColor: '#ffffff',
          textPrimaryColor: '#1e293b',
          textSecondaryColor: '#94a3b8',
          inputBackgroundColor: '#ffffff',
          formBackgroundColor: '#ffffff',
          borderRadiusSmall: '8px',
          borderRadiusMedium: '10px',
          borderRadiusLarge: '12px',
          inputBorderWidth: '1.5px',
          inputFocusedBorderWidth: '2px',
          inputVerticalPadding: '9px',
          inputHorizontalPadding: '14px',
          inputFocusedBoxShadow: '0 0 0 3px rgba(6, 182, 212, 0.28)',
          inputErrorFocusedBoxShadow: '0 0 0 3px rgba(220, 38, 38, 0.22)',
          fontSizeExtraSmall: '12px',
          fontSizeSmall: '13px',
          fontSizeMedium: '15px',
          fontSizeLarge: '16px',
          fontWeightNormal: '400',
          fontWeightSemiBold: '600',
          formPadding: '0px',
          formInputsTextTransform: 'none',
        };

        const callbacks = {
          onReady: () => {
            clearTimeout(readyTimeout);
            initializedAmountRef.current = paymentAmount;
            log('✅ MercadoPago Bricks listo para recibir pagos');
            log('   onReady callback ejecutado correctamente');
            setInitialized(true);
            if (externalSubmitButton) {
              const ctrl = brickControllerRef.current;
              if (ctrl && typeof ctrl.getFormData === 'function') {
                onExternalSubmitReady?.({
                  submit: async () => {
                    const data = await ctrl.getFormData();
                    if (!data?.token) {
                      throw new Error('Completá todos los datos de la tarjeta para continuar.');
                    }
                    return data;
                  },
                });
              } else {
                warn('⚠️ externalSubmitButton: instancia sin getFormData al onReady');
                onExternalSubmitReady?.(null);
              }
            }
            if (onReady) {
              onReady();
            }
          },
          onError: (error) => {
            clearTimeout(readyTimeout);
            if (isBrickValidationError(error)) {
              const validationMessage =
                error?.message || 'Completá todos los datos de la tarjeta para continuar.';
              setMpError(validationMessage);
              return;
            }
            logError('❌ Error en MercadoPago Bricks:', error);
            logError('   Error completo:', JSON.stringify(error, null, 2));
            const errorMessage = error?.message || error?.cause || error?.type || 'Error al procesar el pago';
            setMpError(errorMessage);
            setInitialized(false);
            initializationAttempted.current = false;
            if (externalSubmitButton) {
              onExternalSubmitReady?.(null);
            }
            if (onError) {
              onError(error);
            }
          },
        };

        if (!externalSubmitButton) {
          callbacks.onSubmit = async (formData) => {
            try {
              log('💳 Procesando pago con Bricks...');
              log('   FormData recibido:', formData);
              log('   Token:', formData.token);
              log('   Payment method:', formData.payment_method_id);
              log('   Installments:', formData.installments);

              if (onSuccess) {
                await onSuccess(formData);
              }
            } catch (err) {
              logError('❌ Error procesando pago:', err);
              logError('   Error completo:', JSON.stringify(err, null, 2));
              if (onError) {
                onError(err);
              } else {
                setMpError(err.message || 'Error al procesar el pago');
              }
              throw err;
            }
          };
        }

        const brickConfig = {
          initialization: {
            amount: paymentAmount, // Asegurar que sea número
            currency: currency, // Asegurar que sea string
          },
          customization: {
            paymentMethods: {
              creditCard: 'all',
              debitCard: 'all',
            },
            visual: {
              ...(externalSubmitButton ? { hidePaymentButton: true } : {}),
              style: {
                theme: 'default',
                customVariables: checkoutAlignedBrickVariables,
              },
            },
          },
          callbacks,
        };

        log('📋 Configuración del Brick:', JSON.stringify(brickConfig, null, 2));
        log('   Amount type:', typeof brickConfig.initialization.amount);
        log('   Currency type:', typeof brickConfig.initialization.currency);
        
        // Intentar crear el Brick
        // El método create() devuelve una Promise según la documentación más reciente
        log('🔨 Llamando a bricks().create()...');
        log('   Tipo de contenedor:', typeof containerRef.current);
        log('   Contenedor:', containerRef.current);
        
        // El SDK de MercadoPago requiere el ID sin el símbolo '#'
        // Usar solo el ID del contenedor
        const containerSelector = containerId; // Sin el '#'
        log('   Usando selector (ID sin #):', containerSelector);
        
        try {
          log('   Llamando a create() con selector string...');
          const createResult = bricksBuilderRef.current.create('cardPayment', containerSelector, brickConfig);
          
          // Verificar si es una Promise
          if (createResult && typeof createResult.then === 'function') {
            log('✅ create() devolvió una Promise, esperando resolución...');
            
            createResult
              .then((brickInstance) => {
                log('✅ Promise resuelta, Brick instance recibida:', brickInstance);
                log('   Brick instance type:', typeof brickInstance);
                
                if (brickInstance) {
                  brickControllerRef.current = brickInstance;
                  if (typeof brickInstance === 'object') {
                    brickInstance._readyTimeout = readyTimeout;
                  }
                  log('✅ Brick creado exitosamente, esperando callback onReady...');
                } else {
                  warn('⚠️ Promise resuelta pero no se recibió instancia');
                }
              })
              .catch((createError) => {
                clearTimeout(readyTimeout);
                logError('❌ Error al crear Brick (Promise rechazada):', createError);
                logError('   Mensaje:', createError.message);
                logError('   Stack:', createError.stack);
                setMpError(`Error al crear el formulario de pago: ${createError.message || 'Error desconocido'}`);
                initializationAttempted.current = false;
              });
          } else {
            // Si no es una Promise, tratarlo como objeto directo (versiones antiguas del SDK)
            log('✅ create() devolvió un objeto directo (no Promise)');
            const brickInstance = createResult;
            if (brickInstance && typeof brickInstance === 'object') {
              brickControllerRef.current = brickInstance;
              brickInstance._readyTimeout = readyTimeout;
            }
            log('✅ Brick creado, esperando callback onReady...');
          }
        } catch (syncError) {
          clearTimeout(readyTimeout);
          logError('❌ Error síncrono al crear Brick:', syncError);
          logError('   Mensaje:', syncError.message);
          logError('   Stack:', syncError.stack);
          setMpError(`Error al crear el formulario de pago: ${syncError.message || 'Error desconocido'}`);
          initializationAttempted.current = false;
        }
      } catch (createError) {
        clearTimeout(readyTimeout);
        logError('❌ Error al crear Brick:', createError);
        throw createError;
      }
    } catch (err) {
      logError('❌ Error inicializando MercadoPago Bricks:', err);
      logError('   Detalles:', err.message, err.stack);
      setMpError(`Error al inicializar MercadoPago: ${err.message || 'Error desconocido'}`);
      initializationAttempted.current = false;
    }
  }, [amount, onSuccess, onError, onReady, embedded, externalSubmitButton, onExternalSubmitReady, destroyBrick]);

  useEffect(() => {
    if (!disabled) return undefined;
    destroyBrick();
    setInitialized(false);
    initializationAttempted.current = false;
    onExternalSubmitReady?.(null);
    return undefined;
  }, [disabled, destroyBrick, onExternalSubmitReady]);

  useEffect(() => {
    const paymentAmount = parseFloat(String(amount));
    if (!initialized || !Number.isFinite(paymentAmount) || paymentAmount <= 0) {
      return;
    }
    if (initializedAmountRef.current === paymentAmount) {
      return;
    }

    const ctrl = brickControllerRef.current;
    if (ctrl && typeof ctrl.update === 'function') {
      const updated = ctrl.update({ amount: paymentAmount });
      if (updated) {
        initializedAmountRef.current = paymentAmount;
        log('✅ Monto de Bricks actualizado:', paymentAmount);
        return;
      }
      log('⚠️ Brick update() no aplicó el nuevo monto; el servidor validará el total al pagar.');
      initializedAmountRef.current = paymentAmount;
      return;
    }

    log('⚠️ Brick sin update(); el monto en MP puede quedar desactualizado hasta reiniciar el formulario.');
  }, [amount, initialized, destroyBrick, onExternalSubmitReady]);

  useEffect(() => {
    const checkAndInitialize = () => {
      if (disabled) {
        return;
      }
      if (sdkLoaded && containerRef.current && !initialized && !initializationAttempted.current) {
        const key = getMercadoPagoPublicKey();
        if (key) {
          const isVisible = containerRef.current.offsetParent !== null;
          const isInDOM = document.body.contains(containerRef.current);

          log('🔧 Verificando condiciones para inicializar Bricks...');
          log('   SDK cargado:', sdkLoaded);
          log('   Contenedor existe:', !!containerRef.current);
          log('   Contenedor visible:', isVisible);
          log('   Contenedor en DOM:', isInDOM);
          log('   Ya inicializado:', initialized);
          log('   Intento previo:', initializationAttempted.current);

          if (isVisible && isInDOM) {
            log('✅ Todas las condiciones cumplidas, inicializando Bricks...');
            setTimeout(() => {
              if (containerRef.current && !initialized && !initializationAttempted.current) {
                initializeBricks();
              }
            }, 100);
          } else {
            log('⏳ Esperando que el contenedor esté visible...');
          }
        }
      }
    };

    checkAndInitialize();
    const timer = setTimeout(() => {
      checkAndInitialize();
    }, 500);

    return () => clearTimeout(timer);
  }, [sdkLoaded, initialized, initializeBricks, disabled]);


  useEffect(() => {
    if (!externalSubmitButton) return undefined;
    return () => {
      onExternalSubmitReady?.(null);
    };
  }, [externalSubmitButton, onExternalSubmitReady]);

  if (mpError && !publicKey) {
    return (
      <div className="mp-bricks-error">
        <div className="mp-bricks-error-icon">
          <i className="fas fa-exclamation-triangle" />
        </div>
        <div className="mp-bricks-error-message">
          <h3>Error de Configuración</h3>
          <p>{mpError}</p>
          <p className="mp-bricks-error-help">
            Agrega <code>REACT_APP_MERCADOPAGO_PUBLIC_KEY</code> a tu archivo .env
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`mp-bricks-container${embedded ? ' mp-bricks-container--embedded' : ''}`}>
      {disabled && !initialized && (
        <div className="mp-bricks-loading">
          <div className="mp-bricks-spinner">
            <i className="fas fa-spinner fa-spin" />
          </div>
          <p>Esperando datos de envío para cargar el pago…</p>
        </div>
      )}

      {!disabled && !initialized && !mpError && (
        <div className="mp-bricks-loading">
          <div className="mp-bricks-spinner">
            <i className="fas fa-spinner fa-spin" />
          </div>
          <p>Inicializando MercadoPago...</p>
          <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
            Esto puede tardar unos segundos
          </p>
        </div>
      )}

      {mpError && (
        <div className="mp-bricks-error-banner">
          <i className="fas fa-exclamation-circle" />
          <span>{mpError}</span>
          <button 
            className="mp-bricks-error-close"
            onClick={() => setMpError(null)}
            aria-label="Cerrar error"
          >
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {showTestInfo && (
        <div className="mp-bricks-test-mode">
          <i className="fas fa-flask" />
          <span>Modo de Prueba - Usa tarjetas de prueba</span>
        </div>
      )}

      {/* El contenedor debe estar siempre en el DOM para que el SDK pueda inicializarse */}
      {/* Usamos visibility en lugar de display para que el SDK pueda acceder al elemento */}
      <div 
        className="mp-bricks-payment-form" 
        style={{ 
          visibility: initialized ? 'visible' : 'hidden',
          height: initialized ? 'auto' : '1px',
          overflow: initialized ? 'visible' : 'hidden'
        }}
      >
        <div ref={containerRef} id="mp-bricks-container"></div>
      </div>

      {showTestInfo && initialized && (
        <div className="mp-bricks-test-info">
          <h4>🧪 Tarjetas de Prueba:</h4>
          <ul>
            <li><strong>Visa:</strong> 5031 7557 3453 0604</li>
            <li><strong>Mastercard:</strong> 5031 4332 1540 6351</li>
            <li><strong>CVV:</strong> 123</li>
            <li><strong>Vencimiento:</strong> Cualquier fecha futura</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default MercadoPagoBricks;

